// Code Execution Tool
// Allows AI to write and execute code dynamically for complex tasks
// Uses secure AST-based interpreter (code-executor package)

import { createExecutor } from 'safe-code-executor'
import { createCategoryTools, getTopLevelCategorySummary } from './category-tools.js'

// Tool path prefixes the code executor is allowed to call.
// Blocks access to sensitive tools (e.g. notification) from LLM-generated code.
const ALLOWED_TOOL_PREFIXES = [
  'notes/',
  'web/',
]

function isToolAllowedInCodeExecutor(toolPath) {
  if (!toolPath || typeof toolPath !== 'string') return false
  return ALLOWED_TOOL_PREFIXES.some(prefix => toolPath.startsWith(prefix))
}

/**
 * Get tool API for code execution sandbox
 * @param {Object} ctx - Request context
 * @param {ToolCategory} rootCategory - Root category instance
 * @returns {Object} { tools, innerToolCalls }
 */
const getToolsAPI = (ctx, rootCategory) => {
  const tools = {}
  const innerToolCalls = []

  const categoryTools = createCategoryTools(rootCategory)

  // Read-only operations are unrestricted
  tools.getCategoryInfo = (args) => categoryTools.getCategoryInfo(ctx, args)
  tools.searchTools = (args) => categoryTools.searchTools(ctx, args)

  // executeTool is restricted to whitelisted tool paths
  tools.executeTool = async (args) => {
    if (!isToolAllowedInCodeExecutor(args.toolPath)) {
      throw new Error(`Tool '${args.toolPath}' is not allowed in code executor. Allowed prefixes: ${ALLOWED_TOOL_PREFIXES.join(', ')}`)
    }
    const result = await categoryTools.executeTool(ctx, args)
    innerToolCalls.push({ toolPath: args.toolPath, toolArgs: args.toolArgs, ...result })
    return result
  }

  tools.log = (...args) => console.log('[Code Executor]', ...args)

  return { tools, innerToolCalls }
}

/**
 * Execute user code in a controlled environment
 * @param {Object} ctx - Request context
 * @param {Object} args - Execution parameters
 * @param {string} args.code - JavaScript code to execute
 * @param {number} [args.timeout=30000] - Execution timeout in ms
 * @param {ToolCategory} rootCategory - Root category instance
 * @returns {Promise<Object>} Execution result
 */
export const executeCode = async (ctx, args, rootCategory) => {
  const { code, timeout = 30000 } = args

  if (!code || typeof code !== 'string') {
    throw new Error('Code is required and must be a string')
  }

  const MAX_CODE_SIZE = 50000 // 50KB
  if (code.length > MAX_CODE_SIZE) {
    throw new Error(`Code exceeds maximum size (${MAX_CODE_SIZE} chars)`)
  }

  const maxTimeout = 60000
  const actualTimeout = Math.min(timeout, maxTimeout)

  const { tools, innerToolCalls } = getToolsAPI(ctx, rootCategory)

  const executor = createExecutor({
    timeout: actualTimeout,
    maxIterations: 10000,
    tools,
  })

  const result = await executor.execute(code)

  if (!result.success) {
    console.error('[Code Executor] Execution failed:', result.error)
  }

  return { ...result, innerToolCalls }
}

/**
 * Generate executeCode tool definition with category summary
 * @param {ToolCategory} rootCategory - Root category instance
 * @returns {Object} Tool definition
 */
export function createExecuteCodeDefinition(rootCategory) {
  const categorySummary = getTopLevelCategorySummary(rootCategory)
  const categoryList = categorySummary
    .map((c) => `- ${c.id}: ${c.description}`)
    .join('\n')

  const toolDef = {
    name: 'executeCode',
    description: `Execute JavaScript for local tool access and complex logic. You have a 'tools' object.

Local Categories:
${categoryList}

API:
• tools.executeTool({ toolPath: 'category/tool', toolArgs: {...} }) - Execute a local tool
• tools.searchTools({ query: 'save note' }) - Search local tools
• tools.getCategoryInfo({ categoryPath: 'notes' }) - Browse local tool categories

Example: return await tools.executeTool({ toolPath: 'notes/save', toolArgs: { content: '...' } })

CRITICAL:
- DO NOT wrap code in (async () => {...})() - the executor handles this automatically
- Use await and return directly in code
- Code MUST return a value (not undefined)
- This tool is for LOCAL tools ONLY — use the standalone talkToRole tool for remote roles`,
    parameters: {
      type: 'object',
      properties: {
        code: {
          type: 'string',
          description:
            'JavaScript code to execute. Must be valid JS. Can use async/await. Must return a value.',
        },
        timeout: {
          type: 'number',
          description: 'Execution timeout in milliseconds (default: 30000, max: 60000)',
        },
      },
      required: ['code'],
    },
  }

  return toolDef
}
